type PaymentLike = {
  type?: string | number
  typ?: string | number
  date?: string
  dt?: string
  paymentDateTime?: string
  price?: string | number
  prc?: string | number
  amount?: string | number
  invoiceNetPrice?: string | number
  sumNetPrice?: string | number
  ntp?: string | number
  tbill?: string | number
  traceNo?: string
  refereceNo?: string
  trc?: string
  trackingNumber?: string
  cardNo?: string
  cno?: string
  payerCardNumber?: string
  payerId?: string
  payerNationalCode?: string
  nid?: string
  switchNumber?: string
  swno?: string
  acquirerNumber?: string
  aqno?: string
  terminalNumber?: string
  terno?: string
  txerr?: string
  taxValidationError?: string
  // تاریخ و زمان صدور صورتحساب (شمسی، YYYY/MM/DD HH:mm)؛ فقط برای پرداخت روی صورتحساب ارسال‌شده داده می‌شود
  invoiceIssueDate?: string
  // «اکنون» به وقت تهران (شمسی، YYYY/MM/DD HH:mm)؛ با دادنش، پرداخت با تاریخ آینده خطا می‌گیرد
  nowDateTime?: string
}

// «YYYY/MM/DD HH:mm» با ارقام انگلیسی؛ زمانِ نداشته 00:00 می‌شود تا مقایسهٔ رشته‌ای درست باشد
export const normalizePaymentDateTime = (value: unknown) => {
  const raw = toEnglishDigits(value).replace(/-/g, '/').replace(/\s+/g, ' ')
  const match = raw.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})(?:\s+(\d{1,2}):(\d{2}))?/)
  if (!match) {
    return ''
  }
  const pad = (part: string) => part.padStart(2, '0')
  return `${match[1]}/${pad(match[2])}/${pad(match[3])} ${pad(match[4] || '0')}:${match[5] || '00'}`
}

export const isPaymentDateBeforeIssue = (paymentDate: unknown, invoiceIssueDate: unknown) => {
  const payment = normalizePaymentDateTime(paymentDate)
  const issue = normalizePaymentDateTime(invoiceIssueDate)
  return !!payment && !!issue && payment < issue
}

export const PAYMENT_BEFORE_ISSUE_ERROR =
  'تاریخ و زمان پرداخت قبل از تاریخ و زمان صدور صورتحساب است؛ سامانه مؤدیان پرداخت پیش از صدور را نمی‌پذیرد.'

export const PAYMENT_IN_FUTURE_ERROR =
  'تاریخ و زمان پرداخت بعد از زمان حال است؛ سامانه مؤدیان پرداخت با تاریخ آینده را نمی‌پذیرد.'

// «اکنون» به وقت تهران در تقویم شمسی، تا دقیقه («YYYY/MM/DD HH:mm»)
export const nowPersianDateTime = (now: Date | number = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US-u-ca-persian', {
    timeZone: 'Asia/Tehran',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
    .formatToParts(new Date(now))
    .reduce<Record<string, string>>((values, part) => {
      if (['year', 'month', 'day', 'hour', 'minute'].includes(part.type)) {
        values[part.type] = part.value
      }
      return values
    }, {})
  return normalizePaymentDateTime(
    `${parts.year}/${parts.month}/${parts.day} ${parts.hour}:${parts.minute}`
  )
}

// پرداختی که بعد از «اکنون» تاریخ خورده هنوز انجام نشده است (خطای رسمی ۱۰۰۱۳)
export const isPaymentDateInFuture = (paymentDate: unknown, nowDateTime: unknown) => {
  const payment = normalizePaymentDateTime(paymentDate)
  const now = normalizePaymentDateTime(nowDateTime)
  return !!payment && !!now && payment > now
}

const faDigits = '۰۱۲۳۴۵۶۷۸۹'
const arDigits = '٠١٢٣٤٥٦٧٨٩'

const text = (value: unknown) => (value == null ? '' : String(value).trim())

const toEnglishDigits = (value: unknown) =>
  text(value)
    .replace(/[۰-۹]/g, (digit) => faDigits.indexOf(digit).toString())
    .replace(/[٠-٩]/g, (digit) => arDigits.indexOf(digit).toString())

const toNumber = (value: unknown) => {
  const normalized = toEnglishDigits(value).replace(/[,،٬\s]/g, '')
  return normalized ? Number(normalized) : NaN
}

const digitsOnly = (value: unknown) => toEnglishDigits(value).replace(/\D/g, '')

const exactDigits = (value: unknown, length: number, padLeft = false) => {
  let digits = digitsOnly(value)
  if (!digits) {
    return ''
  }
  if (padLeft && digits.length <= length) {
    digits = digits.padStart(length, '0')
  }
  return digits.length === length ? digits : ''
}

const maxDigits = (value: unknown, maxLength: number) => {
  const digits = digitsOnly(value)
  if (!digits) {
    return ''
  }
  return digits.length <= maxLength ? digits : ''
}

const addError = (errors: string[], message: string) => {
  if (!errors.includes(message)) {
    errors.push(message)
  }
}

const getPaymentType = (payment: PaymentLike) => Number(payment.type ?? payment.typ)

const getPaymentPrice = (payment: PaymentLike) =>
  toNumber(payment.price ?? payment.prc ?? payment.amount)

const getInvoiceNetPrice = (payment: PaymentLike) =>
  toNumber(payment.invoiceNetPrice ?? payment.sumNetPrice ?? payment.ntp ?? payment.tbill)

export const getPaymentTaxValidationErrors = (payment: PaymentLike) => {
  const errors: string[] = []
  const type = getPaymentType(payment)
  const date = text(payment.date ?? payment.dt ?? payment.paymentDateTime)
  const price = getPaymentPrice(payment)
  const invoiceNetPrice = getInvoiceNetPrice(payment)

  if (!Number.isInteger(type) || type < 1 || type > 8) {
    addError(errors, 'فیلد «روش پرداخت» باید یکی از مقادیر 1 تا 8 دستورالعمل باشد.')
  }

  if (!date) {
    addError(errors, 'فیلد «تاریخ و زمان پرداخت» معتبر نیست.')
  } else if (isPaymentDateBeforeIssue(date, payment.invoiceIssueDate)) {
    addError(errors, PAYMENT_BEFORE_ISSUE_ERROR)
  } else if (isPaymentDateInFuture(date, payment.nowDateTime)) {
    addError(errors, PAYMENT_IN_FUTURE_ERROR)
  }

  if (!Number.isFinite(price) || price < 0 || Math.floor(price).toString().length > 18) {
    addError(
      errors,
      'فیلد «مبلغ پرداختی» باید عدد صحیح با حداکثر 18 رقم و صفر یا بزرگ‌تر از صفر باشد.'
    )
  }
  if (
    Number.isFinite(price) &&
    Number.isFinite(invoiceNetPrice) &&
    Math.floor(price) > Math.floor(invoiceNetPrice)
  ) {
    addError(errors, 'فیلد «مبلغ پرداختی» باید برابر یا کوچک‌تر از مبلغ کل صورتحساب باشد.')
  }

  if (type === 4) {
    if (!exactDigits(payment.acquirerNumber ?? payment.aqno, 14, true)) {
      addError(errors, 'فیلد «شماره پذیرنده فروشگاهی» باید دقیقا 14 رقم عددی باشد.')
    }
    if (!exactDigits(payment.terminalNumber ?? payment.terno, 8, true)) {
      addError(errors, 'فیلد «شماره پایانه» باید دقیقا 8 رقم عددی باشد.')
    }
    if (!exactDigits(payment.cardNo ?? payment.cno ?? payment.payerCardNumber, 16)) {
      addError(errors, 'فیلد «شماره کارت پرداخت کننده صورتحساب» باید دقیقا 16 رقم عددی باشد.')
    }
  } else {
    if (
      text(payment.switchNumber ?? payment.swno) &&
      !exactDigits(payment.switchNumber ?? payment.swno, 9)
    ) {
      addError(errors, 'فیلد «شماره سوئیچ پرداخت» باید دقیقا 9 رقم عددی باشد.')
    }
    if (
      text(payment.acquirerNumber ?? payment.aqno) &&
      !exactDigits(payment.acquirerNumber ?? payment.aqno, 14, true)
    ) {
      addError(errors, 'فیلد «شماره پذیرنده فروشگاهی» باید دقیقا 14 رقم عددی باشد.')
    }
    if (
      text(payment.terminalNumber ?? payment.terno) &&
      !exactDigits(payment.terminalNumber ?? payment.terno, 8, true)
    ) {
      addError(errors, 'فیلد «شماره پایانه» باید دقیقا 8 رقم عددی باشد.')
    }
    if (
      text(payment.traceNo ?? payment.refereceNo ?? payment.trc ?? payment.trackingNumber) &&
      !maxDigits(payment.traceNo ?? payment.refereceNo ?? payment.trc ?? payment.trackingNumber, 14)
    ) {
      addError(errors, 'فیلد «شماره پیگیری/شماره مرجع» باید عددی و حداکثر 14 رقم باشد.')
    }
    if (
      text(payment.cardNo ?? payment.cno ?? payment.payerCardNumber) &&
      !exactDigits(payment.cardNo ?? payment.cno ?? payment.payerCardNumber, 16)
    ) {
      addError(errors, 'فیلد «شماره کارت پرداخت کننده صورتحساب» باید دقیقا 16 رقم عددی باشد.')
    }
    if (
      text(payment.payerNationalCode ?? payment.payerId ?? payment.nid) &&
      !maxDigits(payment.payerNationalCode ?? payment.payerId ?? payment.nid, 12)
    ) {
      addError(
        errors,
        'فیلد «شماره/شناسه ملی/کد فراگیر پرداخت کننده صورتحساب» باید عددی و حداکثر 12 رقم باشد.'
      )
    }
  }

  return errors
}

export const getPaymentTaxValidationError = (payment: PaymentLike) => {
  const serverError = text(payment.taxValidationError ?? payment.txerr)
  if (serverError) {
    return serverError
  }

  return getPaymentTaxValidationErrors(payment).join(' ')
}
